import { useContext } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { Text, SimpleGrid, Box, Divider, Flex, Button } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import {
  changeLossHistory,
  changeLosses,
  addLoss,
  removeLoss,
} from 'reducers/homePersonalFormReducer';

import isSuperAdmin from 'helpers/isSuperAdmin/isSuperAdmin';
import getDateString from 'helpers/getDateString';

import { AuthContext } from 'contexts/AuthContext';

import { SECTIONS } from 'views/home_form/constants';

const STATUS_OPTIONS = [
  { value: '', label: 'Select status...' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

const LossHistory = () => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const superAdmin = isSuperAdmin(user);

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.loss_history,
  );

  const onDateChange = (date: Date, name: string) => {
    dispatch(changeLossHistory({ name, value: getDateString(date) }));
  };

  const onRadioChange = (value: string, name: string) => {
    dispatch(changeLossHistory({ name, value: value === 'true' }));
  };

  const onLossChange = (event: any, index: number) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changeLosses({ index, name, value: payload }));
  };

  const onLossDateChange = (date: Date, name: string, index: number) => {
    dispatch(changeLosses({ index, name, value: getDateString(date) }));
  };

  const handleAddLoss = () => {
    if (!data.losses || data.losses.length < 4) {
      dispatch(addLoss());
    }
  };

  const handleRemoveLoss = (index: number) => {
    dispatch(removeLoss(index));
  };

  if (!data) return null;

  return (
    <Card mb="20px" id="sectionLossHistory">
      <Flex justify="space-between" align="center" mb="20px">
        <Text fontWeight="bold" fontSize="2xl">
          Loss History
        </Text>
        {superAdmin && (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddLoss}
          >
            Add Loss ({data.losses?.length || 0}/4)
            <Text textColor="red" display="inline" ml="10px">
              (dev mode)
            </Text>
          </Button>
        )}
      </Flex>

      <SimpleGrid columns={4} spacing="20px" mb="20px">
        <DateInput
          section={SECTIONS.LOSS_HISTORY}
          label="Claims History Report Date"
          date={getDateFromString(data.claims_history_report_date)}
          onChange={(date: Date) =>
            onDateChange(date, 'claims_history_report_date')
          }
        />

        <RadioInput
          section={SECTIONS.LOSS_HISTORY}
          label="Known Circumstances to Claim"
          infoText="Does the applicant have any knowledge or information of any fact, circumstance, or situation which could reasonably give rise to a claim which would fall within the scope of the proposed insurance?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_known_circumstances_to_claim)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_known_circumstances_to_claim')
          }
          message={{
            validationId: 'known_circumstances',
            manual: {
              message: 'Please provide details',
              show: data.has_known_circumstances_to_claim,
            },
            compact: true,
          }}
          required
          stacked
        />

        <RadioInput
          section={SECTIONS.LOSS_HISTORY}
          label="Loss in Past 5 Years"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_loss_in_past_5_years)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_loss_in_past_5_years')
          }
          required
          stacked
          disabled
        />
      </SimpleGrid>

      <Divider mb="20px" />

      {data.losses && data.losses.length > 0 ? (
        <>
          <Text fontWeight="semibold" fontSize="lg" mb="15px">
            Loss Details
          </Text>

          {data.losses.map((loss, index) => (
            <Box
              key={index}
              mt={index === 0 ? '0' : '20px'}
              p="15px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="15px"
              >
                <Text fontWeight="medium" fontSize="md">
                  Loss {index + 1}
                </Text>

                {superAdmin && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => handleRemoveLoss(index)}
                  >
                    Remove
                    <Text textColor="red" display="inline" ml="5px">
                      (dev mode)
                    </Text>
                  </Button>
                )}
              </Box>

              <SimpleGrid
                templateColumns="0.8fr 1.2fr 2fr 1fr 1.2fr 1.5fr 1.2fr"
                spacing="15px"
                mb="15px"
              >
                <FormInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Loc #"
                  subLabel={`Loss History ${index + 1}`}
                  name="loc_no"
                  value={loss.loc_no || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                  disabled
                />
                <DateInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Date"
                  subLabel={`Loss History ${index + 1}`}
                  date={getDateFromString(loss.date)}
                  onChange={(date: Date) =>
                    onLossDateChange(date, 'date', index)
                  }
                  required
                  disabled
                />
                <FormInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Cause of Loss"
                  subLabel={`Loss History ${index + 1}`}
                  name="cause_of_loss"
                  value={loss.cause_of_loss || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                  disabled
                />
                <SelectInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Status"
                  subLabel={`Loss History ${index + 1}`}
                  name="status"
                  options={STATUS_OPTIONS}
                  value={loss.status || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                />
                <FormInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Amount Paid"
                  subLabel={`Loss History ${index + 1}`}
                  name="amount_paid"
                  value={loss.amount_paid || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                  disabled
                />
                <FormInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Insurance Company"
                  subLabel={`Loss History ${index + 1}`}
                  name="insurance_company"
                  value={loss.insurance_company || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                />
                <FormInput
                  section={SECTIONS.LOSS_HISTORY}
                  label="Policy Number"
                  subLabel={`Loss History ${index + 1}`}
                  name="policy_number"
                  value={loss.policy_number || ''}
                  onChange={(event: any) => onLossChange(event, index)}
                  required
                />
              </SimpleGrid>
            </Box>
          ))}
        </>
      ) : (
        <Text
          fontSize="md"
          color="gray.600"
          fontStyle="italic"
          textAlign="center"
        >
          No losses reported on the quote
        </Text>
      )}
    </Card>
  );
};

export default LossHistory;
